import * as stylex from "@stylexjs/stylex";
import { scaleOrdinal } from "@visx/scale";
import { TrajectoriesChart } from "charts/TrajectoriesChart";
import { Button } from "components/button";
import Carousel from "components/carousel";
import { CarouselFooter, EmptyInterpretation, formatPerc, formatPercentagePoints, Highlight, InterpretationToken, i18nLookup, makeDisparityGroupLabels, Slide, SlideTitle, ToggleInterpretationButton } from "components/interpretation";
import { Popup } from "components/popup";
import { useConfig } from "config";
import { format } from "d3-format";
import { ExplorerContext } from "domain/explorer";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { graphql } from "#graphql/client";
import { withFigureIOExplorer } from "../components/figure";
import { metadata } from "../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER_CURRENT";
import { COUNTRIES_EXCLUDED_DISPARITY_SIM, countryControlPlaceholder, countryControlTitle, countryIdIso, DISPARITY_CATEGORIES, DISPARITY_GROUPS, disparitiesGroupsLookup, disparityGroupsControlItems, explorerQuery, forceUnwrapCountryOrRegion, GroupLabelLookup, getRegionOrCountryName, goalRule, horizontalAxisControlItems, makeUseControl, RiseDataDecoder, useFigureState, verticalAxisControlItems, verticalAxisControlLabel, viewControlItems, xAxisSubtitleLookup, xAxisTitleLookup, yAxisTitleLookup } from "../domain";
import { useFetch } from "../hooks/useFetch";
import * as M from "../materials";
import { E, O, pipe } from "../prelude";
export * from "../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER_CURRENT";
export const Data = RiseDataDecoder([
    "id",
    "value",
    "country",
    "year",
    "group",
    "facet"
], {});
const disparitiesColorScale = scaleOrdinal({
    domain: DISPARITY_GROUPS,
    range: [
        M.grayscalePalette[5],
        M.q08Palette[7],
        M.q08Palette[3],
        M.q03Palette[7],
        M.q03Palette[3],
        M.q02Palette[7],
        M.q02Palette[3]
    ]
});
const bandColorScale = scaleOrdinal({
    domain: DISPARITY_CATEGORIES,
    range: [
        M.q08Palette[5],
        M.q03Palette[5],
        M.q02Palette[5]
    ]
});
const filterTrajectoriesData = (x)=>x.filter((d)=>d.facet === "AGE" || d.facet === "GRADE" && d.year >= 1 && d.year <= 10);
const useControl = makeUseControl({
    "2-toggle-interpretation": {
        control: "MultiCheckbox",
        defaultValue: []
    },
    "0-view": {
        defaultValue: "countries",
        control: "Radio"
    },
    "0-vertical-axis": {
        defaultValue: "LIT",
        control: "Radio"
    },
    "1-horizontal-axis": {
        control: "Radio",
        defaultValue: "GRADE"
    },
    "1-country": {
        control: "SingleSelect",
        defaultValue: ""
    },
    "1-countries": {
        control: "MultiSelect",
        defaultValue: []
    },
    "2-disparities": {
        control: "MultiCheckbox",
        defaultValue: []
    }
});
const InterpretationCountries = ({ dataset, getCountryName, requestClose, colorScale, chartActions })=>{
    const countries = useControl("1-countries");
    const skill = useControl("0-vertical-axis");
    const horizontalAxis = useControl("1-horizontal-axis");
    const countryControl = countryIdIso.wrap(countries[0]);
    const intl = useIntl();
    const findPoint = useCallback((d)=>horizontalAxis === "AGE" ? d.year === 9 : d.year === 3, [
        horizontalAxis
    ]);
    const grade3Values = useMemo(()=>{
        return Object.fromEntries(dataset.filter(findPoint).map((d)=>[
                d.country,
                d.value || 0
            ]));
    }, [
        dataset,
        findPoint
    ]);
    const { age9CountryValue, age9CountryFailingValue, skillName, country, level } = useMemo(()=>{
        const value = grade3Values[countries[0]];
        const countryColor = colorScale(countries[0]);
        return {
            age9CountryValue: <InterpretationToken chartActions={chartActions} color={countryColor} highlightPoint={dataset.find((p)=>findPoint(p) && p.country === countries[0])} highlightSeries={countries[0]}>
          {formatPerc(value)}
        </InterpretationToken>,
            age9CountryFailingValue: <Highlight>{formatPerc(1 - value)}</Highlight>,
            country: <InterpretationToken chartActions={chartActions} color={colorScale(countries[0])} highlightSeries={countries[0]}>
          {getCountryName(countryControl)}
        </InterpretationToken>,
            skillName: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillName.LIT"]) : intl.formatMessage(i18nLookup["skillName.NUM"])}
        </Highlight>,
            skillVerb: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillVerb.LIT"]) : intl.formatMessage(i18nLookup["skillVerb.NUM"])}
        </Highlight>,
            level: <Highlight>{horizontalAxis === "AGE" ? "Age 9" : "Grade 3"}</Highlight>
        };
    }, [
        grade3Values,
        countries,
        colorScale,
        dataset,
        findPoint,
        chartActions,
        getCountryName,
        countryControl,
        skill,
        intl,
        horizontalAxis
    ]);
    return <Carousel FooterComponent={(footerProps)=><CarouselFooter {...footerProps} onClose={requestClose}/>}>
      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.common.intepretation-title",
        defaultMessage: "Interpretation"
    }))}
        </SlideTitle>
        <FormattedMessage id="fig.learning.trajectories.interpretation.current.trajectories-content" defaultMessage="This trajectory shows the pace at which children learn foundational {skillName} skills in {country}. SDG 4.1.1 expects all children to have acquired foundational {skillName} skills by Grade 3 (which typically corresponds to age 9). Currently, about {age9CountryValue} of children acquire foundational {skillName} skills by {level}, meaning that {age9CountryFailingValue} do not meet the SDG goal. A high-performing country's trajectory will slope steeply upward in the early years of primary school. A flat or moderately upward sloping trajectory indicates a slow pace of learning, and means many children do not achieve the foundational {skillName} skills they need to succeed in later grades." values={{
        skillName: skillName,
        country: country,
        age9CountryValue: age9CountryValue,
        level: level,
        age9CountryFailingValue: age9CountryFailingValue
    }}/>
      </Slide>
    </Carousel>;
};
const ActivateSlide = ({ onActivate })=>{
    return <>
      <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.disparity-deactivated" defaultMessage="Click the button below to activate this simulation, which is currently deactivated."/>
      <Button className={({
        className: "x1oqdnn1"
    }).className} rounded inverted onClick={()=>onActivate()}>
        Activate
      </Button>
    </>;
};
const InterpretationDisparities = ({ dataset, getCountryName, requestClose, colorScale, chartActions })=>{
    const countryRaw = useControl("1-country");
    const skill = useControl("0-vertical-axis");
    const disparities = useControl("2-disparities");
    const [, actions] = useFigureState();
    const handleActivate = (disparityCategory)=>{
        actions.toggleMultiCheckboxItem("2-disparities", disparityCategory);
    };
    const horizontalAxis = useControl("1-horizontal-axis");
    const countryControl = countryIdIso.wrap(countryRaw);
    const intl = useIntl();
    const findPoint = useCallback((d)=>horizontalAxis === "AGE" ? d.year === 9 : d.year === 3, [
        horizontalAxis
    ]);
    const highlightPoints = useMemo(()=>{
        return Object.fromEntries(Object.entries(disparitiesGroupsLookup).map(([category, groupValues])=>{
            const indexes = Object.fromEntries(groupValues.map((gv, i)=>[
                    gv,
                    i
                ]));
            const sorter = (a, b)=>indexes[a?.group ?? 10] < indexes[b?.group ?? 10] ? -1 : indexes[a?.group ?? 10] > indexes[b?.group ?? 10] ? 1 : 0;
            return [
                category,
                dataset.filter((d)=>findPoint(d) && groupValues.includes(d.group)).sort(sorter)
            ];
        }));
    }, [
        dataset,
        findPoint
    ]);
    const highlightPointValues = useMemo(()=>{
        return Object.fromEntries(dataset.filter(findPoint).map((d)=>[
                d.group,
                d.value || 0
            ]));
    }, [
        dataset,
        findPoint
    ]);
    const disparityGroupLabels = useMemo(()=>makeDisparityGroupLabels(intl), [
        intl
    ]);
    const { groups, differences, values, skillName, country, level } = useMemo(()=>{
        const makeDisparityToken = (disparity, index)=>{
            return <InterpretationToken color={colorScale(disparitiesGroupsLookup[disparity][index])} chartActions={chartActions} highlightPoint={highlightPoints[disparity][0]}>
          {disparityGroupLabels[disparity][index]}
        </InterpretationToken>;
        };
        const makeValueToken = (disparity, index)=>{
            const disparityKeys = disparitiesGroupsLookup[disparity];
            return <InterpretationToken chartActions={chartActions} highlightPoint={highlightPoints[disparity][index]} highlightSeries={disparityKeys[index]} color={colorScale(disparityKeys[index])}>
          {formatPerc(Math.abs(highlightPointValues[disparityKeys[index]]))}
        </InterpretationToken>;
        };
        const makeDifferenceToken = (disparity)=>{
            const disparityKeys = disparitiesGroupsLookup[disparity];
            return <InterpretationToken chartActions={chartActions} highlightPoint={highlightPoints[disparity][0]}>
          {formatPercentagePoints(Math.abs(highlightPointValues[disparityKeys[0]] - highlightPointValues[disparityKeys[1]]), intl)}
        </InterpretationToken>;
        };
        const makeStillFailingValueToken = (disparity)=>{
            const disparityKeys = disparitiesGroupsLookup[disparity];
            return <InterpretationToken chartActions={chartActions} highlightPoint={highlightPoints[disparity][0]}>
          {formatPerc(1 - Math.max(highlightPointValues[disparityKeys[0]], highlightPointValues[disparityKeys[1]]))}
        </InterpretationToken>;
        };
        const groups = {
            WEALTH: [
                makeDisparityToken("WEALTH", 0),
                makeDisparityToken("WEALTH", 1)
            ],
            GENDER: [
                makeDisparityToken("GENDER", 0),
                makeDisparityToken("GENDER", 1)
            ],
            GEOGRAPHY: [
                makeDisparityToken("GEOGRAPHY", 0),
                makeDisparityToken("GEOGRAPHY", 1)
            ]
        };
        const differences = {
            WEALTH: makeDifferenceToken("WEALTH"),
            GENDER: makeDifferenceToken("GENDER"),
            GEOGRAPHY: makeDifferenceToken("GEOGRAPHY")
        };
        const values = {
            WEALTH: [
                makeValueToken("WEALTH", 0),
                makeValueToken("WEALTH", 1)
            ],
            GENDER: [
                makeValueToken("GENDER", 0),
                makeValueToken("GENDER", 1)
            ],
            GEOGRAPHY: [
                makeValueToken("GEOGRAPHY", 0),
                makeValueToken("GEOGRAPHY", 1)
            ]
        };
        const stillFailing = {
            WEALTH: makeStillFailingValueToken("WEALTH"),
            GENDER: makeStillFailingValueToken("GENDER"),
            GEOGRAPHY: makeStillFailingValueToken("GEOGRAPHY")
        };
        return {
            groups,
            differences,
            values,
            stillFailing,
            country: <InterpretationToken chartActions={chartActions} highlightSeries="all">
          {getCountryName(countryControl)}
        </InterpretationToken>,
            skillName: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillName.LIT"]) : intl.formatMessage(i18nLookup["skillName.NUM"])}
        </Highlight>,
            skillVerb: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillVerb.LIT"]) : intl.formatMessage(i18nLookup["skillVerb.NUM"])}
        </Highlight>,
            level: <Highlight>Grade 3 (typically age 9)</Highlight>
        };
    }, [
        highlightPointValues,
        colorScale,
        chartActions,
        getCountryName,
        countryControl,
        skill,
        intl,
        disparityGroupLabels,
        highlightPoints
    ]);
    return <Carousel FooterComponent={(footerProps)=><CarouselFooter {...footerProps} onClose={requestClose}/>}>
      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.current.disparities.interpretation-title",
        defaultMessage: "Interpretation"
    }))}
        </SlideTitle>
        <FormattedMessage id="fig.learning.trajectories.interpretation.current.disparities.interpretation-content" defaultMessage="This graph shows the average learning trajectory for all children in {country}, and the learning trajectories for specific groups in {country}." values={{
        country: country
    }}/>
      </Slide>
      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.current.disparities.WEALTH.title",
        defaultMessage: "Rich / Poor"
    }))}
        </SlideTitle>
        {disparities.includes("WEALTH") ? <FormattedMessage id="fig.learning.trajectories.interpretation.current.disparities.WEALTH.content" defaultMessage="At {level}, when SDG 4.1.1 expects all children to have gained foundational {skillName} skills, {0} of {1} and {2} of {3} have gained these skills in {country}, meaning there is a relative difference in learning of {4}." values={{
        level: level,
        skillName: skillName,
        "0": values.WEALTH[0],
        "1": groups.WEALTH[0],
        "2": values.WEALTH[1],
        "3": groups.WEALTH[1],
        country: country,
        "4": differences.WEALTH
    }}/> : <ActivateSlide onActivate={()=>handleActivate("GENDER")}/>}
      </Slide>
      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.disparities.GENDER.title",
        defaultMessage: "Boys / Girls"
    }))}
        </SlideTitle>
        {disparities.includes("GENDER") ? <FormattedMessage id="fig.learning.trajectories.interpretation.current.disparities.GENDER.content" defaultMessage="At {level}, when SDG 4.1.1 expects all children to have gained foundational {skillName} skills, {0} of {1} and {2} of {3} have gained these skills in {country}, meaning there is a relative difference in learning of {4}." values={{
        level: level,
        skillName: skillName,
        "0": values.GENDER[0],
        "1": groups.GENDER[0],
        "2": values.GENDER[1],
        "3": groups.GENDER[1],
        country: country,
        "4": differences.GENDER
    }}/> : <ActivateSlide onActivate={()=>handleActivate("GENDER")}/>}
      </Slide>

      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.current.disparities.GEOGRAPHY.title",
        defaultMessage: "Urban / Rural"
    }))}
        </SlideTitle>
        {disparities.includes("GEOGRAPHY") ? <FormattedMessage id="fig.learning.trajectories.interpretation.current.disparities.GEOGRAPHY.content" defaultMessage="At {level}, when SDG 4.1.1 expects all children to have gained foundational {skillName} skills, {0} of {1} and {2} of {3} have gained these skills in {country}, meaning there is a relative difference in learning of {4}." values={{
        level: level,
        skillName: skillName,
        "0": values.GEOGRAPHY[0],
        "1": groups.GEOGRAPHY[0],
        "2": values.GEOGRAPHY[1],
        "3": groups.GEOGRAPHY[1],
        country: country,
        "4": differences.GEOGRAPHY
    }}/> : <ActivateSlide onActivate={()=>handleActivate("GEOGRAPHY")}/>}
      </Slide>
    </Carousel>;
};
export const Chart = ({ renderMode })=>{
    const [chartData, setChartData] = useState([]);
    const ctx = React.useContext(ExplorerContext);
    const [state, mutate] = ctx.immer;
    const [, actions] = useFigureState();
    const intl = useIntl();
    const env = useConfig();
    // Controls selections
    const interpretation = useControl("2-toggle-interpretation");
    const interpretationToggled = interpretation.length > 0;
    const selectedCountries = useControl("1-countries");
    const view = useControl("0-view");
    const facet = useControl("1-horizontal-axis");
    const skill = useControl("0-vertical-axis");
    const groups = useControl("2-disparities");
    const country = useControl("1-country");
    const toggleInterpretationRef = useRef(null);
    const chartContainerRef = useRef(null);
    const countries = useMemo(()=>{
        switch(view){
            case "countries":
                return selectedCountries?.length ? selectedCountries : [];
            case "disparities":
                return country ? [
                    country
                ] : [];
            default:
                {
                    const _check = view;
                    return _check;
                }
        }
    }, [
        view,
        selectedCountries,
        country
    ]);
    const selectedGroupsWithAll = useMemo(()=>[
            "all",
            ...groups.flatMap((group)=>disparitiesGroupsLookup[group])
        ], [
        groups
    ]);
    const getCountryName = React.useCallback((country)=>getRegionOrCountryName({
            input: {
                country: country
            },
            regions: env.regions,
            countries: env.countries
        }), [
        env
    ]);
    const getCountryInterpretationName = useCallback((country)=>{
        return country === "lmic" ? intl.formatMessage(i18nLookup.averageLowLowerCountries) : getCountryName(country);
    }, [
        getCountryName,
        intl
    ]);
    const selectedCountryName = getCountryName(countryIdIso.wrap(country));
    useEffect(()=>{
        setChartData([]);
    }, [
        view
    ]);
    const fetcher = useCallback(async ()=>{
        return await fetchObservations(countries, selectedGroupsWithAll, facet, skill);
    }, [
        countries,
        facet,
        selectedGroupsWithAll,
        skill
    ]);
    useFetch({
        fetch: fetcher,
        pause: false,
        onFetch: ()=>{
            mutate((draft)=>{
                draft.status = "fetching";
            });
        },
        onSuccess: (x)=>{
            mutate((draft)=>{
                draft.status = "data";
                const trajectories = filterTrajectoriesData(x);
                setChartData(trajectories);
            });
        },
        onError: ()=>{
            mutate((draft)=>{
                if (renderMode !== "static") {
                    draft.status = "error";
                    draft.errorMessage = "Could not load data";
                }
            });
        }
    });
    useEffect(()=>{
        actions.setSelectionControl("2-toggle-interpretation", {
            type: "MultiCheckbox",
            label: "",
            position: "TOOLBAR",
            selected: O.none
        }, [
            {
                value: "toggled",
                Component: (props)=>{
                    const view = useControl("0-view");
                    const country = useControl("1-country");
                    return <ToggleInterpretationButton animation={view === "countries" && countries.length > 0 || view === "disparities" && country !== ""} ref={toggleInterpretationRef} {...props}/>;
                },
                label: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.interpretation.toggle",
                    defaultMessage: "Interpretation"
                }))
            }
        ]);
    }, [
        view,
        country,
        actions,
        intl,
        countries.length
    ]);
    // Update title
    useEffect(()=>{
        if (view === "countries") {
            actions.setTitle(intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.trajectories.countries.title",
                defaultMessage: "Learning trajectories of {countriesList}"
            }), {
                countriesList: countries.map((country)=>getCountryName(countryIdIso.wrap(country))).join(", ")
            }));
        } else {
            actions.setTitle(intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.trajectories.disparities.title",
                defaultMessage: "Learning trajectories in {selectedCountryName}"
            }), {
                selectedCountryName
            }));
        }
    }, [
        actions,
        intl,
        getCountryName,
        countries,
        selectedCountryName,
        view
    ]);
    // Set controls
    useEffect(()=>{
        actions.setSelectionControl("0-vertical-axis", {
            type: "Radio",
            title: intl.formatMessage(verticalAxisControlLabel),
            selected: O.some("LIT"),
            position: "RIGHT"
        }, verticalAxisControlItems(intl));
        actions.setSelectionControl("1-horizontal-axis", {
            type: "Radio",
            title: intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.controls.horizontalAxis.title",
                defaultMessage: "Horizontal Axis"
            })),
            selected: O.some("GRADE"),
            position: "RIGHT"
        }, horizontalAxisControlItems(intl));
        actions.setSelectionControl("0-view", {
            type: "Radio",
            title: intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.controls.trajectories.view.title",
                defaultMessage: "View trajectories for"
            })),
            selected: O.some("countries"),
            position: "LEFT"
        }, viewControlItems(intl));
        if (view === "countries") {
            actions.clearControl("1-country");
            actions.clearControl("2-disparities");
            actions.setSelectionControl("1-countries", {
                type: "MultiSelect",
                title: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.controls.countries.title",
                    defaultMessage: "Countries"
                })),
                selected: country ? O.some([
                    country
                ]) : O.none,
                maxItems: 5,
                label: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.controls.countries.placeholder",
                    defaultMessage: "Select countries"
                })),
                position: "LEFT"
            }, state.controls.countries);
        } else {
            actions.clearControl("1-countries");
            actions.setSelectionControl("1-country", {
                type: "SingleSelect",
                title: intl.formatMessage(countryControlTitle),
                selected: selectedCountries.length > 0 ? O.some(selectedCountries[0]) : O.none,
                position: "LEFT",
                label: intl.formatMessage(countryControlPlaceholder)
            }, state.controls.countries.filter((country)=>!(view === "disparities" && COUNTRIES_EXCLUDED_DISPARITY_SIM.includes(country.value))));
            actions.setSelectionControl("2-disparities", {
                type: "MultiCheckbox",
                title: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.trajectories.controls.groups.title",
                    defaultMessage: "Type of inequality"
                })),
                label: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.trajectories.controls.groups.label",
                    defaultMessage: "Disparities"
                })),
                selected: O.some([
                    "WEALTH"
                ]),
                position: "LEFT"
            }, disparityGroupsControlItems(intl));
        }
    }, [
        actions,
        state.controls.countries,
        selectedCountries,
        country,
        view,
        intl
    ]);
    const seriesAccessor = useMemo(()=>{
        return view === "disparities" ? (d)=>d.group : (d)=>forceUnwrapCountryOrRegion(d.country);
    }, [
        view
    ]);
    const countriesColorScale = scaleOrdinal({
        domain: countries,
        range: M.discretePalette
    });
    const colorScale = view === "disparities" ? disparitiesColorScale : countriesColorScale;
    const getValueLabel = useMemo(()=>{
        return view === "disparities" ? (d)=>intl.formatMessage(GroupLabelLookup[d.group]) : (d)=>getCountryName(d.country);
    }, [
        getCountryName,
        intl,
        view
    ]);
    const handleToggleInterpretation = ()=>{
        actions.updateSelectionControl("2-toggle-interpretation", O.some(interpretationToggled ? [] : [
            "toggled"
        ]));
    };
    const [lockActivePoint, setLockActivePoint] = useState();
    const [highlightedSeries, setHighlightedSeries] = useState();
    const chartActions = useMemo(()=>({
            setHighlightedSeries: setHighlightedSeries,
            setLockActivePoint: setLockActivePoint
        }), []);
    const lineStyler = useCallback((series)=>({
            stroke: colorScale(series),
            strokeOpacity: highlightedSeries !== undefined ? series === highlightedSeries ? 1 : 0.1 : 1,
            strokeDasharray: view === "disparities" && series === "all" ? "6 8" : undefined,
            strokeWidth: 3
        }), [
        colorScale,
        highlightedSeries,
        view
    ]);
    return <motion.div ref={chartContainerRef} style={{
        position: "relative"
    }} initial={true} animate={{
        opacity: state.status === "fetching" ? 0.25 : 1
    }} transition={{
        duration: 1
    }}>
      <Popup open={interpretationToggled} container={chartContainerRef.current} anchor={toggleInterpretationRef.current} anchorPosition={{
        horizontal: "end",
        vertical: "start"
    }} anchorOffset={{
        horizontal: 20,
        vertical: -15
    }} onClose={handleToggleInterpretation} style={{
        width: 275
    }}>
        {view === "countries" ? countries.length === 0 ? <EmptyInterpretation>
              <SlideTitle>
                <FormattedMessage id="fig.learning.trajectories.explorer.empty-title" defaultMessage="Interpretation"/>
              </SlideTitle>
              <FormattedMessage id="fig.learning.trajectories.explorer.empty-select-countries" defaultMessage="Data interpretation is not available until filters are selected. Select countries to get started."/>
            </EmptyInterpretation> : <InterpretationCountries dataset={chartData} colorScale={colorScale} chartActions={chartActions} getCountryName={getCountryInterpretationName} requestClose={()=>handleToggleInterpretation()}/> : view === "disparities" ? !country ? <EmptyInterpretation>
              <SlideTitle>
                <FormattedMessage id="fig.learning.trajectories.explorer.empty-title" defaultMessage="Interpretation"/>
              </SlideTitle>
              <FormattedMessage id="fig.learning.trajectories.explorer.empty-select-country" defaultMessage="Data interpretation is not available until filters are selected. Select a country to get started."/>
            </EmptyInterpretation> : <InterpretationDisparities dataset={chartData} colorScale={colorScale} chartActions={chartActions} getCountryName={getCountryInterpretationName} requestClose={()=>handleToggleInterpretation()}/> : null}
      </Popup>
      <TrajectoriesChart emptyChartMessage={intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.explorer.empty-countries-no-comparison",
        defaultMessage: "Choose countries to get started"
    }))} height={M.chartHeight.m} dataset={chartData} seriesAccessor={seriesAccessor} xAccessor={(d)=>d.year} yAccessor={(d)=>d.value} yAxisTitle={intl.formatMessage(yAxisTitleLookup[skill])} xAxisTitle={intl.formatMessage(xAxisTitleLookup[facet])} xAxisSubtitle={xAxisSubtitleLookup[facet] ? intl.formatMessage(xAxisSubtitleLookup[facet]) : undefined} formatTooltipLabel={getValueLabel} formatTooltipValue={(d)=>d.value === null ? intl.formatMessage(defineMessage({
            id: "fig.label.data.not.available",
            defaultMessage: "Not Available"
        })) : format(".0%")(d.value)} rules={chartData.length > 0 ? [
        goalRule(intl, facet, skill, true)
    ] : []} lineStyle={lineStyler} backgroundSeriesFilter={(series)=>highlightedSeries ? series !== highlightedSeries : false} bands={groups.map((group)=>({
            upper: disparitiesGroupsLookup[group][1],
            lower: disparitiesGroupsLookup[group][0],
            style: {
                fill: bandColorScale(group),
                fillOpacity: 0.2
            }
        }))} lockActivePoint={lockActivePoint} flipTooltip={true} showColorLegend={true} formatLegendLabel={(series)=>view === "disparities" ? series === "all" ? country === "lmic" ? selectedCountryName : <FormattedMessage id="fig.learning.trajectories.explorer.group.label.all" defaultMessage="{selectedCountryName} Average" values={{
            selectedCountryName: selectedCountryName
        }}/> : intl.formatMessage(GroupLabelLookup[series]) : getCountryName(countryIdIso.wrap(series))} legendValues={view === "disparities" ? selectedGroupsWithAll : countries} legendOrientation="horizontal"/>
    </motion.div>;
};
const ObservationsDocument = graphql(`
  query Observations($countries: [String!]!, $groups: [String!]!, $facet: FACET, $skill: SKILL) {
    getObservations(countries: $countries, groups: $groups, facet: $facet, skill: $skill) {
      id
      value
      country
      year
      group
      facet
    }
  }
`);
async function fetchObservations(countries, selectedGroups, selectedFacet, selectedSkill) {
    const { getObservations } = await explorerQuery(ObservationsDocument, {
        countries: countries,
        groups: selectedGroups,
        facet: selectedFacet,
        skill: selectedSkill
    });
    return pipe(Data.decode(getObservations), E.fold((validationErrors)=>{
        throw new Error(`Could not decode the data ${validationErrors.map((e)=>`${e.value}`).join(",")}`);
    }, (x)=>x));
}
export default withFigureIOExplorer({
    metadata,
    Chart,
    size: "main",
    csv: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER.xlsx", import.meta.url).href
});
